<template>
  <v-container>
    <v-card-text class="text--secondary">
      <v-row dense>
        <v-spacer />
        <v-btn
          icon
          small
          @click="$router.push({ name: 'contract', params: { contractId: contract.id } })"
        >
          <v-icon>mdi-open-in-new</v-icon>
        </v-btn>
      </v-row>
      <v-row dense>
        <v-col cols="6">
          <small><b> {{ $t('contract.name') }}</b> </small>
          <p>{{contract.contractName}}</p>
        </v-col>
        <v-col cols="3">
          <small> <b>{{ $tc('contract.area', 1) }}</b> </small>
          <p>{{contract.area ? contract.area.name : ''}}</p>
        </v-col>
        <v-col cols="3">
          <small> <b>{{ $tc(`contract.project.${contract.area.code || 'OFF'}`, 1) }}</b> </small>
          <p>{{projectDisplayName}}</p>
        </v-col>
        <v-col cols="5">
          <small> <b>{{ $tc('contract.package', 1) }}</b> </small>
          <p>{{contract.package}}</p>
        </v-col>
        <v-col cols="4">
          <small> <b>{{ $tc('contract.type', 1) }}</b> </small>
          <p>{{contract.contractType}}</p>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <small> <b>{{ $t('contract.contractManager') }}</b> </small>
          <p>{{contractManager}}</p>
        </v-col>
        <v-col>
          <small> <b>{{ $t('contract.packageManager') }}</b> </small>
          <p>{{packageManager}}</p>
        </v-col>
        <v-col>
          <small> <b>{{ $t('contract.projectController') }}</b> </small>
          <p>{{projectController}}</p>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <small> <b>{{ $tc('contract.employer', 1) }}</b> </small>
          <p>{{employer}}<br /><i>{{employerAddress}}</i></p>
        </v-col>
        <v-col>
          <small> <b>{{ $tc('contract.contractor', 1) }}</b> </small>
          <p>{{contractor}}<br /><i>{{contractorAddress}}</i></p>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <small> <b>{{ $t('contract.signedPrice') }}</b> </small>
          <p>{{ signedPriceCents | formatCurrency(contract.chosenCurrency)}}</p>
        </v-col>
        <v-col>
          <small> <b>{{ $t('contract.adjustedPrice') }}</b> </small>
          <InfoTooltip
            :text="$t('contract.adjustedPriceTip')"
          />
          <p>
            {{ adjustedPriceCents | formatCurrency(contract.chosenCurrency)}}
            {{ $t('contract.percentOfSignedPrice', { percent: adjustedPricePercent }) }}
          </p>
        </v-col>
        <v-col>
          <small> <b>{{ $t('contract.forecastedPrice') }}</b> </small>
          <InfoTooltip
            :text="$t('contract.forecastedPriceTip')"
          />
          <p>
            {{ forecastedPriceCents | formatCurrency(contract.chosenCurrency)}}
            {{ $t('contract.percentOfSignedPrice', { percent: forecastedPricePercent }) }}
          </p>
        </v-col>
        <v-col>
          <small> <b>{{ $t('contract.alreadyPaid') }}</b> </small>
          <p>{{ currentPriceCents | formatCurrency(contract.chosenCurrency)}} ({{ currentPercent }}%)</p>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="4">
          <small> <b>{{ $t('contract.afterNegotiations') }}</b> </small>
          <p>{{ afterNegotiationsPriceCents | formatCurrency(contract.chosenCurrency)}}</p>
        </v-col>
        <v-col cols="5">
          <small> <b>{{ $t('contract.beforeNegotiations') }}</b> </small>
          <p>{{ beforeNegotiationsPriceCents | formatCurrency(contract.chosenCurrency)}}</p>
        </v-col>
        <v-col cols="3">
          <small> <b>{{ $t('contract.savings') }}</b> </small>
          <p>{{ savings }}</p>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="4">
          <small> <b>{{ $tc('contract.currency', 2) }}</b> </small>
          <v-simple-table dense class="text--secondary">
            <tbody>
              <tr
                v-for="(currency, index) in contract.currencies"
                :key="index"
              >
                <td>{{currency.key}}</td>
                <td>{{currency.value}}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
        <v-col>
          <small> <b>{{ $tc(partTranslationKey + '.self', 2) }}</b> </small>
          <v-simple-table dense class="text--secondary">
            <thead>
              <tr>
                <th>{{ $t(partTranslationKey + '.name') }}</th>
                <th>{{ $t('part.ratio') }}</th>
                <th>{{ $t('part.orderNumber') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(part, index) in contract.parts"
                :key="index"
              >
                <td>{{part.name}}</td>
                <td>{{part.ratio}}%</td>
                <td>{{part.purchaseOrder}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-divider v-if="purchaseOrderList.length"/>
          <small v-if="purchaseOrderList.length"> <b>{{ $t('contract.purchaseOrders') }}</b> </small>
          <p
            v-for="(number, index) in purchaseOrderList"
            :key="index"
            class="purchase-order"
          >
            {{number.purchaseOrder}} - {{number.name}}
          </p>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="2">
          <small> <b>{{ $t('contract.signatureDate') }}</b> </small>
          <p>{{ contract.signatureDate | formatDate }}</p>
        </v-col>
        <v-col cols="2">
          <small><b> {{ $t('contract.commencementDate') }}</b> </small>
          <p>{{ contract.commencementDate | formatDate }}</p>
        </v-col>
        <v-col cols="2">
          <small> <b>{{ $t('contract.completionTime') }}</b> </small>
          <p>{{ contract.completionTime | formatDate }}</p>
        </v-col>
        <v-col cols="4">
          <small> <b>{{ $t('contract.adjustedCompletionTime') }}</b> </small>
          <InfoTooltip
            :text="$t('contract.adjustedCompletionTimeTip')"
          />
          <p>{{ contract.adjustedCompletionTime | formatDate }} (+{{adjustedDays}})</p>
        </v-col>
        <v-col cols="2">
          <small><b>{{ $t('contract.finalTakingOver') }}</b></small>
          <p>{{ contract.takingOverDate | formatDate }}</p>
        </v-col>
      </v-row>

    </v-card-text>
  </v-container>
</template>

<script>
import { contractModel } from '@/validators'
import ContractInfoMixin from '@/mixins/ContractInfoMixin'
import { getLocalizationContext } from '@/helpers/translations'

export default {
  name: 'ContractInfo',
  props: {
    contract: {
      type: Object,
      required: true
    }
  },
  mixins: [ContractInfoMixin],
  data () {
    return { }
  },
  computed: {
    partTranslationKey () {
      return getLocalizationContext(this.contract.isMultiCurrency)
    }
  },
  methods: { },
  validations: contractModel
}
</script>

<style>
.purchase-order {
  margin: 5px 0!important;
}
</style>
